import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from "../components/ui/dialog";
import { 
  TrendingUp, TrendingDown, Activity, Calendar, Target, BarChart3, Clock, CheckCircle,
  Settings, ChevronRight, Bell, Mail, AlertTriangle, Zap, Plus, Trash2, X,
  ArrowUpRight, ArrowDownRight, Info
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

// Types for settings and configuration
interface ThresholdSettings {
  highPriorityThreshold: number;
  warningThreshold: number;
  opportunityThreshold: number;
  dailyTarget: number;
  weeklyTarget: number;
}

interface ActionRule {
  id: string;
  name: string;
  condition: 'below_threshold' | 'above_threshold' | 'variance_exceeded';
  threshold: number;
  metric: string;
  action: 'email' | 'notification' | 'sms' | 'webhook';
  recipients: string[];
  enabled: boolean;
}

interface DepartmentData {
  dept: string;
  planned: number;
  actual: number;
  percentage: number;
  trend: number;
  history: { date: string; planned: number; actual: number }[];
}

interface TimeSlotData {
  time: string;
  planned: number;
  actual: number;
  status: 'above' | 'below' | 'on-track';
  variance: number;
  history: { date: string; planned: number; actual: number }[];
}

// Mock data with enhanced details
const departmentData: DepartmentData[] = [
  { 
    dept: "Weighbridge Operations", 
    planned: 120, 
    actual: 108, 
    percentage: 90,
    trend: -2,
    history: [
      { date: "Mon", planned: 120, actual: 115 },
      { date: "Tue", planned: 120, actual: 108 },
      { date: "Wed", planned: 120, actual: 112 },
      { date: "Thu", planned: 120, actual: 105 },
      { date: "Fri", planned: 120, actual: 108 }
    ]
  },
  { 
    dept: "Loading & Unloading", 
    planned: 80, 
    actual: 85, 
    percentage: 106,
    trend: 5,
    history: [
      { date: "Mon", planned: 80, actual: 82 },
      { date: "Tue", planned: 80, actual: 85 },
      { date: "Wed", planned: 80, actual: 88 },
      { date: "Thu", planned: 80, actual: 84 },
      { date: "Fri", planned: 80, actual: 85 }
    ]
  },
  { 
    dept: "Fuel Services", 
    planned: 45, 
    actual: 42, 
    percentage: 93,
    trend: -1,
    history: [
      { date: "Mon", planned: 45, actual: 44 },
      { date: "Tue", planned: 45, actual: 42 },
      { date: "Wed", planned: 45, actual: 43 },
      { date: "Thu", planned: 45, actual: 41 },
      { date: "Fri", planned: 45, actual: 42 }
    ]
  },
  { 
    dept: "Maintenance Area", 
    planned: 25, 
    actual: 18, 
    percentage: 72,
    trend: -8,
    history: [
      { date: "Mon", planned: 25, actual: 20 },
      { date: "Tue", planned: 25, actual: 18 },
      { date: "Wed", planned: 25, actual: 19 },
      { date: "Thu", planned: 25, actual: 17 },
      { date: "Fri", planned: 25, actual: 18 }
    ]
  },
  { 
    dept: "Administrative", 
    planned: 30, 
    actual: 35, 
    percentage: 117,
    trend: 8,
    history: [
      { date: "Mon", planned: 30, actual: 32 },
      { date: "Tue", planned: 30, actual: 35 },
      { date: "Wed", planned: 30, actual: 34 },
      { date: "Thu", planned: 30, actual: 36 },
      { date: "Fri", planned: 30, actual: 35 }
    ]
  }
];

const timeSlotData: TimeSlotData[] = [
  { time: "06:00 - 08:00", planned: 15, actual: 12, status: "below", variance: -20, history: [] },
  { time: "08:00 - 10:00", planned: 20, actual: 23, status: "above", variance: 15, history: [] },
  { time: "10:00 - 12:00", planned: 25, actual: 24, status: "on-track", variance: -4, history: [] },
  { time: "12:00 - 14:00", planned: 18, actual: 15, status: "below", variance: -17, history: [] },
  { time: "14:00 - 16:00", planned: 22, actual: 26, status: "above", variance: 18, history: [] },
  { time: "16:00 - 18:00", planned: 12, actual: 8, status: "below", variance: -33, history: [] }
];

export default function PlanVsActual() {
  const { toast } = useToast();
  
  // Settings state
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState<ThresholdSettings>({
    highPriorityThreshold: 80,
    warningThreshold: 90,
    opportunityThreshold: 105,
    dailyTarget: 50,
    weeklyTarget: 350
  });
  const [tempSettings, setTempSettings] = useState<ThresholdSettings>(settings);
  
  // Action rules state
  const [actionRulesOpen, setActionRulesOpen] = useState(false);
  const [actionRules, setActionRules] = useState<ActionRule[]>([
    {
      id: '1',
      name: 'Underperformance Alert',
      condition: 'below_threshold',
      threshold: 80,
      metric: 'Department Performance',
      action: 'email',
      recipients: ['manager@navig8.com'],
      enabled: true
    },
    {
      id: '2',
      name: 'Evening Shift Warning',
      condition: 'below_threshold',
      threshold: 70,
      metric: 'Time Slot Performance',
      action: 'notification',
      recipients: ['operations@navig8.com'],
      enabled: true
    }
  ]);
  const [newRule, setNewRule] = useState<Partial<ActionRule>>({
    condition: 'below_threshold',
    threshold: 80,
    action: 'email',
    recipients: [],
    enabled: true
  });
  const [newRecipient, setNewRecipient] = useState('');
  
  // Detail modal states
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedWidget, setSelectedWidget] = useState<string | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<DepartmentData | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlotData | null>(null);
  
  // KPI detail modal state
  const [kpiDetailOpen, setKpiDetailOpen] = useState(false);
  const [selectedKpi, setSelectedKpi] = useState<string | null>(null);

  // Calculate actual performance based on targets
  const dailyActual = 31;
  const weeklyActual = 298;
  const dailyPercentage = Math.round((dailyActual / settings.dailyTarget) * 100);
  const weeklyPercentage = Math.round((weeklyActual / settings.weeklyTarget) * 100);

  // Handle settings save
  const handleSaveSettings = () => {
    setSettings(tempSettings);
    setSettingsOpen(false);
    toast({
      title: "Settings Saved",
      description: "Your threshold and target settings have been updated."
    });
  };

  // Handle adding new action rule
  const handleAddRule = () => {
    if (!newRule.name || !newRule.metric) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }
    
    const rule: ActionRule = {
      id: Date.now().toString(),
      name: newRule.name || '',
      condition: newRule.condition || 'below_threshold',
      threshold: newRule.threshold || 80,
      metric: newRule.metric || '',
      action: newRule.action || 'email',
      recipients: newRule.recipients || [],
      enabled: newRule.enabled ?? true
    };
    
    setActionRules([...actionRules, rule]);
    setNewRule({
      condition: 'below_threshold',
      threshold: 80,
      action: 'email',
      recipients: [],
      enabled: true
    });
    
    toast({
      title: "Rule Created",
      description: `Action rule "${rule.name}" has been created.`
    });
  };

  // Handle delete action rule
  const handleDeleteRule = (id: string) => {
    setActionRules(actionRules.filter(r => r.id !== id));
    toast({
      title: "Rule Deleted",
      description: "Action rule has been removed."
    });
  };

  // Handle toggle action rule
  const handleToggleRule = (id: string) => {
    setActionRules(actionRules.map(r => 
      r.id === id ? { ...r, enabled: !r.enabled } : r
    ));
  };

  // Open KPI detail modal
  const openKpiDetail = (kpi: string) => {
    setSelectedKpi(kpi);
    setKpiDetailOpen(true);
  };

  // Open department detail modal
  const openDepartmentDetail = (dept: DepartmentData) => {
    setSelectedDepartment(dept);
    setDetailModalOpen(true);
    setSelectedWidget('department');
  };

  // Open time slot detail modal
  const openTimeSlotDetail = (slot: TimeSlotData) => {
    setSelectedTimeSlot(slot);
    setDetailModalOpen(true);
    setSelectedWidget('timeslot');
  };

  // Get badge variant based on thresholds
  const getBadgeVariant = (percentage: number) => {
    if (percentage >= settings.opportunityThreshold) return "default";
    if (percentage >= settings.warningThreshold) return "secondary";
    if (percentage >= settings.highPriorityThreshold) return "secondary";
    return "destructive";
  };

  // Get status color based on thresholds
  const getStatusColor = (percentage: number) => {
    if (percentage >= settings.opportunityThreshold) return "text-green-400";
    if (percentage >= settings.warningThreshold) return "text-yellow-400";
    if (percentage >= settings.highPriorityThreshold) return "text-orange-400";
    return "text-red-400";
  };

  return (
    <div>
      <div className="space-y-6 p-6">
        {/* Header with Settings */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-[hsl(187,100%,42%)]">Plan vs Actual</h2>
            <p className="text-muted-foreground">Compare planned visitor targets with actual performance</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button 
              variant="outline" 
              onClick={() => setActionRulesOpen(true)}
              data-testid="button-action-rules"
            >
              <Zap className="w-4 h-4 mr-2" />
              Action Rules
            </Button>
            <Button 
              variant="outline" 
              onClick={() => {
                setTempSettings(settings);
                setSettingsOpen(true);
              }}
              data-testid="button-settings"
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button variant="outline" data-testid="button-export">
              <BarChart3 className="w-4 h-4 mr-2" />
              Export Report
            </Button>
            <Button data-testid="button-refresh">
              <Activity className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Key Performance Indicators - Clickable */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card 
            className="cursor-pointer hover-elevate transition-all"
            onClick={() => openKpiDetail('daily')}
            data-testid="card-daily-target"
          >
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Daily Target</CardTitle>
              <div className="flex items-center gap-1">
                <Target className="h-4 w-4 text-[hsl(187,100%,42%)]" />
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{settings.dailyTarget}</div>
              <div className="flex items-center text-xs text-muted-foreground">
                <span className={dailyPercentage >= 100 ? "text-green-400 font-medium" : dailyPercentage >= 80 ? "text-yellow-400 font-medium" : "text-red-400 font-medium"}>
                  {dailyActual} actual
                </span>
                <span className="mx-1">|</span>
                <span>{dailyPercentage}% achieved</span>
              </div>
              <Progress value={Math.min(dailyPercentage, 100)} className="mt-2" data-testid="progress-daily" />
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover-elevate transition-all"
            onClick={() => openKpiDetail('weekly')}
            data-testid="card-weekly-target"
          >
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Weekly Target</CardTitle>
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4 text-green-400" />
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{settings.weeklyTarget}</div>
              <div className="flex items-center text-xs text-muted-foreground">
                <span className={weeklyPercentage >= 100 ? "text-green-400 font-medium" : weeklyPercentage >= 80 ? "text-yellow-400 font-medium" : "text-red-400 font-medium"}>
                  {weeklyActual} actual
                </span>
                <span className="mx-1">|</span>
                <span>{weeklyPercentage}% achieved</span>
              </div>
              <Progress value={Math.min(weeklyPercentage, 100)} className="mt-2" data-testid="progress-weekly" />
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover-elevate transition-all"
            onClick={() => openKpiDetail('efficiency')}
            data-testid="card-efficiency"
          >
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Efficiency Rate</CardTitle>
              <div className="flex items-center gap-1">
                <TrendingUp className="h-4 w-4 text-green-400" />
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-400">92%</div>
              <p className="text-xs text-muted-foreground flex items-center">
                <ArrowUpRight className="w-3 h-3 mr-1 text-green-400" />
                +5% from last week
              </p>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover-elevate transition-all"
            onClick={() => openKpiDetail('variance')}
            data-testid="card-variance"
          >
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Variance</CardTitle>
              <div className="flex items-center gap-1">
                <TrendingDown className="h-4 w-4 text-red-400" />
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-400">-15%</div>
              <p className="text-xs text-muted-foreground flex items-center">
                <ArrowDownRight className="w-3 h-3 mr-1 text-red-400" />
                Below target
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Department Performance - Clickable Items */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-[hsl(187,100%,42%)]">Department Performance</CardTitle>
              <CardDescription>Click on any department for detailed analysis</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {departmentData.map((item, index) => (
                  <div 
                    key={index} 
                    className="space-y-2 p-3 rounded-lg cursor-pointer hover-elevate transition-all border border-transparent hover:border-border"
                    onClick={() => openDepartmentDetail(item)}
                    data-testid={`dept-performance-${index}`}
                  >
                    <div className="flex justify-between items-center gap-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{item.dept}</span>
                        {item.trend > 0 ? (
                          <ArrowUpRight className="w-3 h-3 text-green-400" />
                        ) : (
                          <ArrowDownRight className="w-3 h-3 text-red-400" />
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {item.actual}/{item.planned}
                        </span>
                        <Badge 
                          variant={getBadgeVariant(item.percentage)}
                          data-testid={`badge-dept-${index}`}
                        >
                          {item.percentage}%
                        </Badge>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                    <Progress value={Math.min(item.percentage, 100)} className="h-2" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-[hsl(187,100%,42%)]">Time-based Analysis</CardTitle>
              <CardDescription>Click on any time slot for trend analysis</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {timeSlotData.map((slot, index) => (
                  <div 
                    key={index} 
                    className="flex items-center justify-between gap-2 p-3 border rounded-lg cursor-pointer hover-elevate transition-all"
                    onClick={() => openTimeSlotDetail(slot)}
                    data-testid={`time-slot-${index}`}
                  >
                    <div className="flex items-center gap-3">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{slot.time}</p>
                        <p className="text-sm text-muted-foreground">
                          Planned: {slot.planned} | Actual: {slot.actual}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${slot.variance > 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {slot.variance > 0 ? '+' : ''}{slot.variance}%
                      </span>
                      {slot.status === "above" && <TrendingUp className="h-4 w-4 text-green-400" />}
                      {slot.status === "below" && <TrendingDown className="h-4 w-4 text-red-400" />}
                      {slot.status === "on-track" && <CheckCircle className="h-4 w-4 text-[hsl(187,100%,42%)]" />}
                      <Badge 
                        variant={slot.status === "above" ? "default" : slot.status === "on-track" ? "secondary" : "destructive"}
                        data-testid={`badge-time-${index}`}
                      >
                        {slot.status.replace("-", " ")}
                      </Badge>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Metrics */}
        <Card>
          <CardHeader>
            <CardTitle className="text-[hsl(187,100%,42%)]">Performance Metrics</CardTitle>
            <CardDescription>Detailed analysis of visitor flow and facility utilization</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <h4 className="font-semibold text-sm text-muted-foreground">VISITOR THROUGHPUT</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm">Average Processing Time</span>
                    <span className="font-medium">12 minutes</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Peak Hour Capacity</span>
                    <span className="font-medium">85%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Queue Wait Time</span>
                    <span className="font-medium text-green-400">3 minutes</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold text-sm text-muted-foreground">FACILITY UTILIZATION</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm">Weighbridge A</span>
                    <span className="font-medium">78%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Weighbridge B</span>
                    <span className="font-medium">65%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Loading Bays</span>
                    <span className="font-medium text-red-400">92%</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold text-sm text-muted-foreground">COMPLIANCE METRICS</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm">On-time Arrivals</span>
                    <span className="font-medium text-green-400">87%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Documentation Complete</span>
                    <span className="font-medium">94%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Safety Compliance</span>
                    <span className="font-medium text-green-400">98%</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Items - Enhanced */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
            <div>
              <CardTitle className="text-[hsl(187,100%,42%)]">Action Items</CardTitle>
              <CardDescription>Recommended actions to improve plan vs actual performance</CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setActionRulesOpen(true)}
              data-testid="button-configure-actions"
            >
              <Settings className="w-4 h-4 mr-2" />
              Configure Actions
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <h4 className="font-semibold text-sm text-red-400">HIGH PRIORITY</h4>
                <div className="space-y-2">
                  {departmentData.filter(d => d.percentage < settings.highPriorityThreshold).map((dept, i) => (
                    <div 
                      key={i}
                      className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg cursor-pointer hover-elevate"
                      onClick={() => openDepartmentDetail(dept)}
                      data-testid={`action-high-priority-dept-${i}`}
                    >
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-400" />
                        <p className="font-medium text-red-400">{dept.dept} Underperformance</p>
                      </div>
                      <p className="text-sm text-red-400/80 mt-1">
                        {100 - dept.percentage}% below target - investigate staffing and equipment issues
                      </p>
                    </div>
                  ))}
                  {timeSlotData.filter(t => t.variance <= -30).map((slot, i) => (
                    <div 
                      key={`time-${i}`}
                      className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg cursor-pointer hover-elevate"
                      onClick={() => openTimeSlotDetail(slot)}
                      data-testid={`action-high-priority-time-${i}`}
                    >
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-red-400" />
                        <p className="font-medium text-red-400">{slot.time} Shortfall</p>
                      </div>
                      <p className="text-sm text-red-400/80 mt-1">
                        {slot.time} slot consistently underperforming ({slot.variance}%)
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-semibold text-sm text-green-400">OPPORTUNITIES</h4>
                <div className="space-y-2">
                  {departmentData.filter(d => d.percentage >= settings.opportunityThreshold).map((dept, i) => (
                    <div 
                      key={i}
                      className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg cursor-pointer hover-elevate"
                      onClick={() => openDepartmentDetail(dept)}
                      data-testid={`action-opportunity-dept-${i}`}
                    >
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-green-400" />
                        <p className="font-medium text-green-400">{dept.dept} Optimization</p>
                      </div>
                      <p className="text-sm text-green-400/80 mt-1">
                        {dept.percentage - 100}% above target - consider expanding capacity
                      </p>
                    </div>
                  ))}
                  {timeSlotData.filter(t => t.variance >= 15).map((slot, i) => (
                    <div 
                      key={`time-${i}`}
                      className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg cursor-pointer hover-elevate"
                      onClick={() => openTimeSlotDetail(slot)}
                      data-testid={`action-opportunity-time-${i}`}
                    >
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-green-400" />
                        <p className="font-medium text-green-400">{slot.time} Peak Efficiency</p>
                      </div>
                      <p className="text-sm text-green-400/80 mt-1">
                        Successfully handling {slot.variance}% above planned volume
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Action Rules Summary */}
        {actionRules.filter(r => r.enabled).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-[hsl(187,100%,42%)] flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Active Action Rules
              </CardTitle>
              <CardDescription>Automated actions configured for performance events</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {actionRules.filter(r => r.enabled).map(rule => (
                  <Badge 
                    key={rule.id} 
                    variant="outline" 
                    className="py-1.5 px-3 cursor-pointer"
                    onClick={() => setActionRulesOpen(true)}
                    data-testid={`badge-active-rule-${rule.id}`}
                  >
                    {rule.action === 'email' && <Mail className="w-3 h-3 mr-1" />}
                    {rule.action === 'notification' && <Bell className="w-3 h-3 mr-1" />}
                    {rule.name}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[hsl(187,100%,42%)]">Performance Settings</DialogTitle>
            <DialogDescription>
              Configure targets and threshold levels for performance monitoring
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="targets" className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="targets">Targets</TabsTrigger>
              <TabsTrigger value="thresholds">Thresholds</TabsTrigger>
            </TabsList>
            
            <TabsContent value="targets" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="dailyTarget" className="text-[hsl(187,100%,42%)]">Daily Target</Label>
                <Input
                  id="dailyTarget"
                  type="number"
                  value={tempSettings.dailyTarget}
                  onChange={(e) => setTempSettings({ ...tempSettings, dailyTarget: parseInt(e.target.value) || 0 })}
                  data-testid="input-daily-target"
                />
                <p className="text-xs text-muted-foreground">Expected number of visitors per day</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="weeklyTarget" className="text-[hsl(187,100%,42%)]">Weekly Target</Label>
                <Input
                  id="weeklyTarget"
                  type="number"
                  value={tempSettings.weeklyTarget}
                  onChange={(e) => setTempSettings({ ...tempSettings, weeklyTarget: parseInt(e.target.value) || 0 })}
                  data-testid="input-weekly-target"
                />
                <p className="text-xs text-muted-foreground">Expected number of visitors per week</p>
              </div>
            </TabsContent>
            
            <TabsContent value="thresholds" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="highPriority" className="text-red-400">High Priority Threshold (%)</Label>
                <Input
                  id="highPriority"
                  type="number"
                  value={tempSettings.highPriorityThreshold}
                  onChange={(e) => setTempSettings({ ...tempSettings, highPriorityThreshold: parseInt(e.target.value) || 0 })}
                  data-testid="input-high-priority"
                />
                <p className="text-xs text-muted-foreground">Performance below this triggers high priority alerts</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="warning" className="text-yellow-400">Warning Threshold (%)</Label>
                <Input
                  id="warning"
                  type="number"
                  value={tempSettings.warningThreshold}
                  onChange={(e) => setTempSettings({ ...tempSettings, warningThreshold: parseInt(e.target.value) || 0 })}
                  data-testid="input-warning"
                />
                <p className="text-xs text-muted-foreground">Performance below this shows warning status</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="opportunity" className="text-green-400">Opportunity Threshold (%)</Label>
                <Input
                  id="opportunity"
                  type="number"
                  value={tempSettings.opportunityThreshold}
                  onChange={(e) => setTempSettings({ ...tempSettings, opportunityThreshold: parseInt(e.target.value) || 0 })}
                  data-testid="input-opportunity"
                />
                <p className="text-xs text-muted-foreground">Performance above this indicates optimization opportunities</p>
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveSettings} data-testid="button-save-settings">Save Settings</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Rules Dialog */}
      <Dialog open={actionRulesOpen} onOpenChange={setActionRulesOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[hsl(187,100%,42%)]">Action Rules Configuration</DialogTitle>
            <DialogDescription>
              Set up automated actions when performance thresholds are triggered
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Existing Rules */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm">Existing Rules</h4>
              {actionRules.length === 0 ? (
                <p className="text-sm text-muted-foreground">No action rules configured yet.</p>
              ) : (
                <div className="space-y-2">
                  {actionRules.map(rule => (
                    <div 
                      key={rule.id} 
                      className={`p-3 border rounded-lg ${rule.enabled ? 'border-border' : 'border-border/50 opacity-60'}`}
                    >
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={rule.enabled}
                            onCheckedChange={() => handleToggleRule(rule.id)}
                            data-testid={`switch-rule-${rule.id}`}
                          />
                          <div>
                            <p className="font-medium">{rule.name}</p>
                            <p className="text-xs text-muted-foreground">
                              When {rule.metric} is {rule.condition.replace('_', ' ')} {rule.threshold}%, 
                              send {rule.action} to {rule.recipients.length} recipient(s)
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteRule(rule.id)}
                          data-testid={`button-delete-rule-${rule.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add New Rule */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="font-semibold text-sm text-[hsl(187,100%,42%)]">Add New Rule</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[hsl(187,100%,42%)]">Rule Name</Label>
                  <Input
                    placeholder="e.g., Underperformance Alert"
                    value={newRule.name || ''}
                    onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                    data-testid="input-new-rule-name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[hsl(187,100%,42%)]">Metric</Label>
                  <Select
                    value={newRule.metric}
                    onValueChange={(v) => setNewRule({ ...newRule, metric: v })}
                  >
                    <SelectTrigger data-testid="select-metric">
                      <SelectValue placeholder="Select metric" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Department Performance">Department Performance</SelectItem>
                      <SelectItem value="Time Slot Performance">Time Slot Performance</SelectItem>
                      <SelectItem value="Daily Target">Daily Target</SelectItem>
                      <SelectItem value="Weekly Target">Weekly Target</SelectItem>
                      <SelectItem value="Efficiency Rate">Efficiency Rate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[hsl(187,100%,42%)]">Condition</Label>
                  <Select
                    value={newRule.condition}
                    onValueChange={(v: 'below_threshold' | 'above_threshold' | 'variance_exceeded') => setNewRule({ ...newRule, condition: v })}
                  >
                    <SelectTrigger data-testid="select-condition">
                      <SelectValue placeholder="Select condition" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="below_threshold">Below Threshold</SelectItem>
                      <SelectItem value="above_threshold">Above Threshold</SelectItem>
                      <SelectItem value="variance_exceeded">Variance Exceeded</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[hsl(187,100%,42%)]">Threshold (%)</Label>
                  <Input
                    type="number"
                    value={newRule.threshold || 80}
                    onChange={(e) => setNewRule({ ...newRule, threshold: parseInt(e.target.value) || 80 })}
                    data-testid="input-new-rule-threshold"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[hsl(187,100%,42%)]">Action Type</Label>
                  <Select
                    value={newRule.action}
                    onValueChange={(v: 'email' | 'notification' | 'sms' | 'webhook') => setNewRule({ ...newRule, action: v })}
                  >
                    <SelectTrigger data-testid="select-action-type">
                      <SelectValue placeholder="Select action" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Send Email</SelectItem>
                      <SelectItem value="notification">Push Notification</SelectItem>
                      <SelectItem value="sms">Send SMS</SelectItem>
                      <SelectItem value="webhook">Trigger Webhook</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[hsl(187,100%,42%)]">Add Recipient</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="email@example.com"
                      value={newRecipient}
                      onChange={(e) => setNewRecipient(e.target.value)}
                      data-testid="input-recipient"
                    />
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => {
                        if (newRecipient && !newRule.recipients?.includes(newRecipient)) {
                          setNewRule({ ...newRule, recipients: [...(newRule.recipients || []), newRecipient] });
                          setNewRecipient('');
                        }
                      }}
                      data-testid="button-add-recipient"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {(newRule.recipients?.length ?? 0) > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {newRule.recipients?.map((r, i) => (
                        <Badge key={i} variant="secondary" className="gap-1">
                          {r}
                          <X 
                            className="w-3 h-3 cursor-pointer" 
                            onClick={() => setNewRule({ ...newRule, recipients: newRule.recipients?.filter((_, idx) => idx !== i) })}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <Button onClick={handleAddRule} className="w-full" data-testid="button-add-rule">
                <Plus className="w-4 h-4 mr-2" />
                Add Action Rule
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* KPI Detail Modal */}
      <Dialog open={kpiDetailOpen} onOpenChange={setKpiDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[hsl(187,100%,42%)]">
              {selectedKpi === 'daily' && 'Daily Target Details'}
              {selectedKpi === 'weekly' && 'Weekly Target Details'}
              {selectedKpi === 'efficiency' && 'Efficiency Rate Details'}
              {selectedKpi === 'variance' && 'Variance Analysis'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {selectedKpi === 'daily' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">Target</p>
                    <p className="text-2xl font-bold">{settings.dailyTarget}</p>
                  </div>
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">Actual</p>
                    <p className="text-2xl font-bold">{dailyActual}</p>
                  </div>
                </div>
                <Progress value={dailyPercentage} className="h-3" />
                <p className="text-center text-sm text-muted-foreground">{dailyPercentage}% of daily target achieved</p>
                
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm">Hourly Breakdown</h4>
                  {timeSlotData.slice(0, 4).map((slot, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span>{slot.time}</span>
                      <span className={slot.actual >= slot.planned ? 'text-green-400' : 'text-red-400'}>
                        {slot.actual}/{slot.planned}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
            
            {selectedKpi === 'weekly' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">Target</p>
                    <p className="text-2xl font-bold">{settings.weeklyTarget}</p>
                  </div>
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">Actual</p>
                    <p className="text-2xl font-bold">{weeklyActual}</p>
                  </div>
                </div>
                <Progress value={weeklyPercentage} className="h-3" />
                <p className="text-center text-sm text-muted-foreground">{weeklyPercentage}% of weekly target achieved</p>
                
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm">Daily Trend (This Week)</h4>
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map((day, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span>{day}</span>
                      <span className="text-green-400">{50 + Math.floor(Math.random() * 15)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
            
            {selectedKpi === 'efficiency' && (
              <>
                <div className="text-center p-6 bg-green-500/10 rounded-lg">
                  <p className="text-4xl font-bold text-green-400">92%</p>
                  <p className="text-sm text-green-400/80">Current Efficiency Rate</p>
                </div>
                
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm">Efficiency Factors</h4>
                  <div className="flex justify-between text-sm">
                    <span>Processing Speed</span>
                    <span className="text-green-400">95%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Queue Management</span>
                    <span className="text-green-400">88%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Resource Utilization</span>
                    <span className="text-yellow-400">93%</span>
                  </div>
                </div>
              </>
            )}
            
            {selectedKpi === 'variance' && (
              <>
                <div className="text-center p-6 bg-red-500/10 rounded-lg">
                  <p className="text-4xl font-bold text-red-400">-15%</p>
                  <p className="text-sm text-red-400/80">Below Target</p>
                </div>
                
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm">Variance Contributors</h4>
                  <div className="flex justify-between text-sm">
                    <span>Maintenance Area</span>
                    <span className="text-red-400">-28%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Evening Shift (16:00-18:00)</span>
                    <span className="text-red-400">-33%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Early Morning (06:00-08:00)</span>
                    <span className="text-red-400">-20%</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Department/TimeSlot Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-lg">
          {selectedWidget === 'department' && selectedDepartment && (
            <>
              <DialogHeader>
                <DialogTitle className="text-[hsl(187,100%,42%)]">{selectedDepartment.dept}</DialogTitle>
                <DialogDescription>Detailed performance analysis</DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 bg-muted rounded-lg text-center">
                    <p className="text-xs text-muted-foreground">Planned</p>
                    <p className="text-xl font-bold">{selectedDepartment.planned}</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg text-center">
                    <p className="text-xs text-muted-foreground">Actual</p>
                    <p className="text-xl font-bold">{selectedDepartment.actual}</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg text-center">
                    <p className="text-xs text-muted-foreground">Performance</p>
                    <p className={`text-xl font-bold ${getStatusColor(selectedDepartment.percentage)}`}>
                      {selectedDepartment.percentage}%
                    </p>
                  </div>
                </div>
                
                <Progress value={Math.min(selectedDepartment.percentage, 100)} className="h-3" />
                
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Weekly Trend:</span>
                  {selectedDepartment.trend > 0 ? (
                    <span className="flex items-center text-green-400">
                      <ArrowUpRight className="w-4 h-4" /> +{selectedDepartment.trend}%
                    </span>
                  ) : (
                    <span className="flex items-center text-red-400">
                      <ArrowDownRight className="w-4 h-4" /> {selectedDepartment.trend}%
                    </span>
                  )}
                </div>
                
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm">5-Day History</h4>
                  {selectedDepartment.history.map((h, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span>{h.date}</span>
                      <span className={h.actual >= h.planned ? 'text-green-400' : 'text-red-400'}>
                        {h.actual}/{h.planned} ({Math.round((h.actual / h.planned) * 100)}%)
                      </span>
                    </div>
                  ))}
                </div>
                
                {selectedDepartment.percentage < settings.highPriorityThreshold && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                      <span className="text-sm font-medium text-red-400">Action Required</span>
                    </div>
                    <p className="text-xs text-red-400/80 mt-1">
                      This department is below the high priority threshold. Consider investigating staffing and equipment issues.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
          
          {selectedWidget === 'timeslot' && selectedTimeSlot && (
            <>
              <DialogHeader>
                <DialogTitle className="text-[hsl(187,100%,42%)]">{selectedTimeSlot.time}</DialogTitle>
                <DialogDescription>Time slot performance analysis</DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 bg-muted rounded-lg text-center">
                    <p className="text-xs text-muted-foreground">Planned</p>
                    <p className="text-xl font-bold">{selectedTimeSlot.planned}</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg text-center">
                    <p className="text-xs text-muted-foreground">Actual</p>
                    <p className="text-xl font-bold">{selectedTimeSlot.actual}</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg text-center">
                    <p className="text-xs text-muted-foreground">Variance</p>
                    <p className={`text-xl font-bold ${selectedTimeSlot.variance > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {selectedTimeSlot.variance > 0 ? '+' : ''}{selectedTimeSlot.variance}%
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center justify-center gap-2">
                  <Badge variant={selectedTimeSlot.status === 'above' ? 'default' : selectedTimeSlot.status === 'on-track' ? 'secondary' : 'destructive'}>
                    {selectedTimeSlot.status === 'above' && <TrendingUp className="w-3 h-3 mr-1" />}
                    {selectedTimeSlot.status === 'below' && <TrendingDown className="w-3 h-3 mr-1" />}
                    {selectedTimeSlot.status === 'on-track' && <CheckCircle className="w-3 h-3 mr-1" />}
                    {selectedTimeSlot.status.replace('-', ' ').toUpperCase()}
                  </Badge>
                </div>
                
                {selectedTimeSlot.variance <= -30 && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                      <span className="text-sm font-medium text-red-400">Significant Shortfall</span>
                    </div>
                    <p className="text-xs text-red-400/80 mt-1">
                      This time slot is consistently underperforming. Consider adjusting staffing or investigating bottlenecks.
                    </p>
                  </div>
                )}
                
                {selectedTimeSlot.variance >= 15 && (
                  <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-green-400" />
                      <span className="text-sm font-medium text-green-400">Peak Performance</span>
                    </div>
                    <p className="text-xs text-green-400/80 mt-1">
                      This time slot is exceeding targets. Consider this a model for other underperforming slots.
                    </p>
                  </div>
                )}
                
                <div className="p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Info className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Recommendations</span>
                  </div>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>Review historical patterns for this time slot</li>
                    <li>Compare with similar performing slots</li>
                    <li>Consider resource reallocation</li>
                  </ul>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
